/**
 * KK-507 / KK-520 · Stok sayımı — İKİ ADIMLI ve bu ayrım ekranın omurgasıdır.
 *
 *   POST /sayim            → sayımı AÇAR, stoğa DOKUNMAZ.
 *   POST /sayim/:id/kapat  → farkı sayim_farki (tip 4) hareketi olarak YAZAR.
 *
 * NEDEN TEK ADIM DEĞİL:
 *   Sayım, defter (sistem miktarı) ile rafın karşılaştırılmasıdır. Karşılaştırma
 *   ile düzeltme aynı anda yapılırsa fark hiç GÖRÜLMEDEN yok olur; oysa farkın
 *   kendisi sayımın ürünüdür. Açma adımı yalnızca "o an defterde ne yazıyordu"
 *   bilgisini dondurur ve farkı gösterir; sayan kişi rakamı görüp doğrular,
 *   kapanışta fark açık bir hareket olarak yazılır ve v_nokta_sorumluluk
 *   üzerinden noktanın sorumluluğuna geçer (KK-520 / KK-521). FARK ASLA
 *   SESSİZCE DÜZELTİLMEZ.
 *
 * AÇIK SAYIM NE DEMEK:
 *   durum = 1 olan, yani KAPANMAMIŞ sayımdır — farkı henüz YAZILMAMIŞ sayım.
 *   Sayfa yenilendiğinde sayim_id kaybolduğu için sunucuya GET /sayim ucu
 *   eklendi: açık sayım kaybolmaz, "Açık sayımlar" sekmesinden bulunup
 *   kapatılır. Kapatılmayan sayım = hiç yazılmayan fark. Yanlış açılan sayım
 *   İPTAL edilir (POST /sayim/:id/iptal, durum = 3): fark yazılmaz, stok
 *   değişmez; satırlar denetim için kalır.
 *
 * ADR-03: miktarlar STRING'dir. Bu dosyada Number()/parseFloat() ve miktar
 * aritmetiği YOKTUR; fark sunucudan hazır gelir, burada yalnızca gösterilir.
 */
import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alan,
  Buton,
  Kart,
  Rozet,
  Secim,
  Tablo,
  TabloBaslik,
  TabloGovde,
  Uyari,
} from "@kantin/ui";
import {
  STOK_DURUM_ADI,
  STOK_YETKILI_ROLLER,
  StokDurumu,
} from "@kantin/shared";
import { useYetkili } from "../../veri/oturum.js";
import { apiOku, apiYaz, kapsamDisiMi, yetkisizMi } from "./api.js";
import { UrunSecimi } from "./UrunSecimi.js";
import {
  barkodBul,
  noktaSecenekleri,
  useNoktalar,
  useUrunler,
} from "./kancalar.js";
import type { BarkodSonucu } from "./ortak.js";
import {
  BarkodAlani,
  IslemDurumu,
  MetinAlani,
  Sayfa,
  SayfaBasligi,
  Sayfalayici,
  Sekmeler,
  SorguDurumu,
  TehlikeliOnay,
  miktarGecerliMi,
  miktarGoster,
  miktarNormalize,
  tarihSaatGoster,
} from "./ortak.js";

// ---------------------------------------------------------------------------
// Tipler — apps/api/src/moduller/stok.rotalari.ts yanıt şekilleri
// ---------------------------------------------------------------------------

interface SayimBasligi {
  id: string;
  nokta_id: string;
  nokta_ad: string;
  personel_id: string | null;
  personel_ad: string | null;
  durum: number;
  /** acik === (durum === 1) — sunucu ikisini de gönderir. */
  acik: boolean;
  aciklama: string | null;
  baslangic_zamani: string;
  kapanis_zamani: string | null;
  satir_sayisi: number;
  farkli_satir_sayisi: number;
}

interface SayimSatiri {
  urun_id: string;
  urun_ad: string;
  stok_durumu: number;
  /** ADR-03: miktar alanları string'dir. */
  sistem_miktar: string;
  sayilan_miktar: string;
  fark: string;
}

interface SayimDetayi extends SayimBasligi {
  satirlar: SayimSatiri[];
}

/** Karantina.tsx ile aynı şekil — GET /stok yanıtı. */
interface StokDagilimi {
  nokta_id: string;
  nokta_ad: string;
  nokta_tur: number;
  depo_mu: boolean;
  durum: number;
  durum_adi: string;
  miktar: string;
}

interface StokUrunu {
  urun_id: string;
  urun_ad: string;
  toplam: string;
  kondisyon: Record<string, string>;
  dagilim: StokDagilimi[];
}

/** sayim.durum — 1 açık, 2 kapandı, 3 iptal (0005_urun_stok.sql, 0025). */
const SAYIM_ACIK = 1;
const SAYIM_KAPANDI = 2;
const SAYIM_IPTAL = 3;

/** zSayimGovdesi.satirlar üst sınırı (stok.rotalari.ts). */
const SATIR_UST_SINIR = 2000;
const GECMIS_BOYUT = 20;

type Sekme = "acik" | "yeni" | "gecmis";

// ---------------------------------------------------------------------------
// Fark yardımcıları — ham sayıya çevirmeden, YALNIZCA string üzerinde
// ---------------------------------------------------------------------------

/** "0.000" / "-0.000" sıfırdır; sıfırdan farklı olması için bir rakam yeter. */
function farkSifirMi(fark: string): boolean {
  return !/[1-9]/.test(fark);
}

/** İşaret için sayıya çevirmeye gerek yok: eksi fark "-" ile başlar. */
function farkEksiMi(fark: string): boolean {
  return fark.trimStart().startsWith("-");
}

function kondisyonAdi(durum: number): string {
  return STOK_DURUM_ADI[durum] ?? "Bilinmiyor";
}

// ---------------------------------------------------------------------------

export function Sayim() {
  const [sekme, setSekme] = useState<Sekme>("acik");
  const [acikSayim, setAcikSayim] = useState<string | null>(null);
  const [gecmisSayim, setGecmisSayim] = useState<string | null>(null);
  const [yeniOlusan, setYeniOlusan] = useState<string | null>(null);

  // KK-1214 / §5.1: sayım açmak ve kapatmak STOK_YETKILI_ROLLER işidir; API'nin
  // okuma ucu da aynı kümeyi ister, bu yüzden yetkisiz rol ekranı hiç görmez.
  const yetkili = useYetkili(...STOK_YETKILI_ROLLER);

  const acikSorgu = useQuery({
    queryKey: ["sayim", "liste", SAYIM_ACIK],
    queryFn: () =>
      apiOku<{ sayimlar: SayimBasligi[] }>("/sayim", {
        durum: SAYIM_ACIK,
        limit: 200,
      }),
    enabled: yetkili,
  });

  if (!yetkili) {
    return (
      <Sayfa>
        <SayfaBasligi baslik="Sayım" />
        <Uyari tur="hata" baslik="Bu ekranı görüntüleme yetkiniz yok">
          <p className="m-0">
            Sayım açmak ve kapatmak stok hareketi yazar; yalnızca nokta
            görevlisi, kantin müdürü, okul yönetimi ve okul sistem yöneticisi
            yapabilir (§5.1). <strong>Kasiyer rolü sayım kapatamaz</strong> —
            kapanış defter ile raf arasındaki farkı kalıcı olarak yazar.
          </p>
        </Uyari>
      </Sayfa>
    );
  }

  if (acikSorgu.error && yetkisizMi(acikSorgu.error)) {
    return (
      <Sayfa>
        <SayfaBasligi baslik="Sayım" />
        <Uyari tur="hata" baslik="Sayım kayıtlarına erişemiyorsunuz">
          <p className="m-0">
            Sunucu bu kayıtları size göstermiyor. Yetkiniz yalnızca atandığınız
            noktaları kapsıyor olabilir; nokta ataması için okul yönetimine
            başvurun.
          </p>
        </Uyari>
      </Sayfa>
    );
  }

  const acikSayimlar = acikSorgu.data?.sayimlar ?? [];

  return (
    <Sayfa>
      <SayfaBasligi
        baslik="Sayım"
        aciklama="Defter ile rafın karşılaştırılması. Sayımı açmak stoğa dokunmaz; fark yalnızca kapanışta stok hareketi olarak yazılır."
      />

      <IkiAdim />

      <Sekmeler<Sekme>
        etiket="Sayım bölümleri"
        secili={sekme}
        sec={setSekme}
        sekmeler={[
          { deger: "acik", metin: "Açık sayımlar", sayi: acikSayimlar.length },
          { deger: "yeni", metin: "Yeni sayım" },
          { deger: "gecmis", metin: "Geçmiş" },
        ]}
      />

      {sekme === "acik" && (
        <AcikSayimlar
          sorgu={acikSorgu}
          sayimlar={acikSayimlar}
          acik={acikSayim}
          ac={setAcikSayim}
          yeniOlusan={yeniOlusan}
        />
      )}

      {sekme === "yeni" && (
        <YeniSayim
          olusturuldu={(id) => {
            setYeniOlusan(id);
            setAcikSayim(id);
            setSekme("acik");
          }}
        />
      )}

      {sekme === "gecmis" && (
        <GecmisSayimlar acik={gecmisSayim} ac={setGecmisSayim} />
      )}
    </Sayfa>
  );
}

// ---------------------------------------------------------------------------

function IkiAdim() {
  return (
    <Kart baslik="Sayım iki adımdır" sikisik>
      <ol className="m-0 flex list-none flex-wrap items-stretch gap-2 p-0">
        <li className="flex min-w-[14rem] flex-1 items-center gap-2">
          <div className="flex-1 rounded-md border border-cetvel bg-kagit-2 px-3 py-2">
            <p className="m-0 text-sm font-semibold text-murekkep">
              1 · Sayımı aç
            </p>
            <p className="m-0 text-xs text-murekkep-3">
              Raftaki miktar girilir, sistem miktarı dondurulur.{" "}
              <strong>Stok değişmez.</strong>
            </p>
          </div>
          <span aria-hidden="true" className="text-murekkep-3">
            →
          </span>
        </li>
        <li className="flex min-w-[14rem] flex-1 items-center gap-2">
          <div className="flex-1 rounded-lg border border-hata-cizgi bg-hata-yumusak px-3 py-2">
            <p className="m-0 text-sm font-semibold text-murekkep">
              2 · Sayımı kapat
            </p>
            <p className="m-0 text-xs text-murekkep-2">
              Her farklı satır için <strong>sayım farkı</strong> hareketi
              yazılır ve noktanın sorumluluğuna geçer. Geri alınamaz.
            </p>
          </div>
        </li>
      </ol>
    </Kart>
  );
}

// ---------------------------------------------------------------------------

function AcikSayimlar({
  sorgu,
  sayimlar,
  acik,
  ac,
  yeniOlusan,
}: {
  sorgu: { isPending: boolean; error: unknown; refetch: () => unknown };
  sayimlar: SayimBasligi[];
  acik: string | null;
  ac: (id: string | null) => void;
  yeniOlusan: string | null;
}) {
  // Bant yalnızca yeni açılan sayım HÂLÂ açıkken anlamlıdır; kapanınca ya da
  // iptal edilince listeden düşer, bant da düşer.
  const yeniHalaAcik =
    yeniOlusan !== null && sayimlar.some((s) => s.id === yeniOlusan);

  return (
    <>
      {yeniHalaAcik && (
        <Uyari tur="onay" baslik="Sayım açıldı, henüz kapanmadı">
          Fark tablosu aşağıda. Sayım <strong>açık kaldığı sürece</strong> stok
          hareketi yazılmaz ve kaybolmaz; şimdi kapatmazsanız bu sekmeden devam
          edebilirsiniz.
        </Uyari>
      )}

      <Kart
        baslik="Açık sayımlar"
        ustBilgi={
          <span className="text-sm text-murekkep-3">
            <span className="rakam font-mono">{sayimlar.length}</span> kayıt
          </span>
        }
      >
        <SorguDurumu
          yukleniyor={sorgu.isPending}
          hata={sorgu.error}
          bos={sayimlar.length === 0}
          bosBaslik="Açık sayım yok"
          bosAciklama="Kapatılmayı bekleyen sayım bulunmuyor. Yeni sayım sekmesinden bir nokta sayabilirsiniz."
          yenile={() => void sorgu.refetch()}
        >
          <SayimTablosu
            sayimlar={sayimlar}
            acik={acik}
            ac={ac}
            kapanis={false}
          />
        </SorguDurumu>
      </Kart>

      {acik && <SayimDetayPaneli sayimId={acik} />}
    </>
  );
}

// ---------------------------------------------------------------------------

function SayimTablosu({
  sayimlar,
  acik,
  ac,
  kapanis,
}: {
  sayimlar: SayimBasligi[];
  acik: string | null;
  ac: (id: string | null) => void;
  kapanis: boolean;
}) {
  return (
    <Tablo>
      <TabloBaslik>
        <th scope="col">Nokta</th>
        <th scope="col">Personel</th>
        <th scope="col">{kapanis ? "Kapanış / iptal" : "Başlangıç"}</th>
        <th scope="col">Satır</th>
        <th scope="col">Farklı satır</th>
        <th scope="col">
          <span className="gizli-erisilebilir">İşlem</span>
        </th>
      </TabloBaslik>
      <TabloGovde>
        {sayimlar.map((s) => (
          <tr key={s.id}>
            <td className="font-semibold text-murekkep">{s.nokta_ad}</td>
            <td>{s.personel_ad ?? "—"}</td>
            <td>
              {tarihSaatGoster(kapanis ? s.kapanis_zamani : s.baslangic_zamani)}
            </td>
            <td className="rakam text-right font-mono">{s.satir_sayisi}</td>
            <td className="text-right">
              {s.farkli_satir_sayisi === 0 ? (
                <span className="rakam font-mono text-murekkep-3">0</span>
              ) : (
                <Rozet tur="uyari">
                  <span className="rakam font-mono">
                    {s.farkli_satir_sayisi}
                  </span>
                </Rozet>
              )}
            </td>
            <td className="text-right">
              <Buton
                tur="ikincil"
                onClick={() => ac(acik === s.id ? null : s.id)}
                aria-expanded={acik === s.id}
              >
                {acik === s.id ? "Kapat" : "Aç"}
              </Buton>
            </td>
          </tr>
        ))}
      </TabloGovde>
    </Tablo>
  );
}

// ---------------------------------------------------------------------------

function SayimDetayPaneli({ sayimId }: { sayimId: string }) {
  const istemci = useQueryClient();
  const [onayAcik, setOnayAcik] = useState(false);
  const [iptalOnayAcik, setIptalOnayAcik] = useState(false);

  const sorgu = useQuery({
    queryKey: ["sayim", sayimId],
    queryFn: () => apiOku<SayimDetayi>(`/sayim/${sayimId}`),
  });

  const kapat = useMutation({
    mutationFn: () =>
      apiYaz<{ sayim_id: string; nokta_id: string; fark_sayisi: number }>(
        "POST",
        `/sayim/${sayimId}/kapat`,
      ),
    onSuccess: () => {
      setOnayAcik(false);
      // Kapanış stok hareketi yazar: hem sayım listeleri hem stok bayatlar.
      void istemci.invalidateQueries({ queryKey: ["sayim"] });
      void istemci.invalidateQueries({ queryKey: ["stok"] });
    },
  });

  // İptal stoğa DOKUNMAZ; yalnızca sayım listeleri bayatlar.
  const iptal = useMutation({
    mutationFn: () =>
      apiYaz<{ sayim_id: string; nokta_id: string; durum: number }>(
        "POST",
        `/sayim/${sayimId}/iptal`,
      ),
    onSuccess: () => {
      setIptalOnayAcik(false);
      void istemci.invalidateQueries({ queryKey: ["sayim"] });
    },
  });

  const s = sorgu.data;
  const farkliSatirlar = (s?.satirlar ?? []).filter(
    (r) => !farkSifirMi(r.fark),
  );

  // KK-1508: kapsam dışı kayıt 404/BULUNAMADI döner, yani SorguDurumu'nun HATA
  // dalına düşer — `bos` dalı hiç çalışmaz. Ham "Veriler yüklenemedi" yerine
  // kullanıcıya ne olduğunu söylemek için ayrı ele alınır.
  if (sorgu.error && kapsamDisiMi(sorgu.error)) {
    return (
      <Kart baslik="Sayım farkı">
        <Uyari tur="uyari" baslik="Sayım görüntülenemiyor">
          <p className="m-0">
            Kayıt silinmiş ya da yetki kapsamınız dışında bir noktaya ait
            olabilir. Yalnızca sorumlusu olduğunuz noktaların sayımlarını
            görebilirsiniz.
          </p>
        </Uyari>
      </Kart>
    );
  }

  return (
    <Kart baslik="Sayım farkı">
      <SorguDurumu
        yukleniyor={sorgu.isPending}
        hata={sorgu.error}
        bos={!s}
        bosBaslik="Sayım bulunamadı"
        iskeletSatir={4}
        yenile={() => void sorgu.refetch()}
      >
        {s && (
          <div className="flex flex-col gap-4">
            <dl className="m-0 grid gap-3 text-sm md:grid-cols-4">
              <div>
                <dt className="text-xs text-murekkep-3">Nokta</dt>
                <dd className="m-0 text-murekkep">{s.nokta_ad}</dd>
              </div>
              <div>
                <dt className="text-xs text-murekkep-3">Sayan personel</dt>
                <dd className="m-0 text-murekkep">{s.personel_ad ?? "—"}</dd>
              </div>
              <div>
                <dt className="text-xs text-murekkep-3">Başlangıç</dt>
                <dd className="m-0 text-murekkep">
                  {tarihSaatGoster(s.baslangic_zamani)}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-murekkep-3">Durum</dt>
                <dd className="m-0">
                  {s.acik ? (
                    <Rozet tur="uyari">Açık — fark yazılmadı</Rozet>
                  ) : s.durum === SAYIM_IPTAL ? (
                    <Rozet tur="notr">
                      İptal edildi · {tarihSaatGoster(s.kapanis_zamani)}
                    </Rozet>
                  ) : (
                    <Rozet tur="onay">
                      Kapandı · {tarihSaatGoster(s.kapanis_zamani)}
                    </Rozet>
                  )}
                </dd>
              </div>
            </dl>

            {s.aciklama && (
              <p className="m-0 text-sm text-murekkep-2">{s.aciklama}</p>
            )}

            <Tablo>
              <TabloBaslik>
                <th scope="col">Ürün</th>
                <th scope="col">Kondisyon</th>
                <th scope="col">Sistem</th>
                <th scope="col">Sayılan</th>
                <th scope="col">Fark</th>
              </TabloBaslik>
              <TabloGovde>
                {s.satirlar.map((r) => {
                  const sifir = farkSifirMi(r.fark);
                  return (
                    <tr key={`${r.urun_id}|${r.stok_durumu}`}>
                      <td className="font-semibold text-murekkep">
                        {r.urun_ad}
                      </td>
                      <td>{kondisyonAdi(r.stok_durumu)}</td>
                      <td className="rakam text-right font-mono">
                        {miktarGoster(r.sistem_miktar)}
                      </td>
                      <td className="rakam text-right font-mono">
                        {miktarGoster(r.sayilan_miktar)}
                      </td>
                      <td className="text-right">
                        {sifir ? (
                          <span className="rakam font-mono text-murekkep-3">
                            0
                          </span>
                        ) : (
                          // Eksi fark = rafta defterden AZ mal var (kayıp);
                          // artı fark = defterde görünmeyen mal bulundu.
                          <Rozet tur={farkEksiMi(r.fark) ? "hata" : "bilgi"}>
                            <span className="rakam font-mono">
                              {miktarGoster(r.fark)}
                            </span>
                          </Rozet>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </TabloGovde>
            </Tablo>

            {kapat.data && (
              <Uyari tur="onay" baslik="Sayım kapandı">
                <span className="rakam font-mono">
                  {kapat.data.fark_sayisi}
                </span>{" "}
                satır için sayım farkı hareketi yazıldı. Fark artık noktanın
                sorumluluğundadır (KK-520).
              </Uyari>
            )}

            {iptal.data && (
              <Uyari tur="bilgi" baslik="Sayım iptal edildi">
                Fark yazılmadı, stok değişmedi. Satırlar denetim için saklanır;
                sayım Geçmiş sekmesinde &quot;İptal&quot; olarak görünür.
              </Uyari>
            )}

            {iptal.error &&
            (yetkisizMi(iptal.error) || kapsamDisiMi(iptal.error)) ? (
              <Uyari tur="hata" baslik="Sayımı iptal etme yetkiniz yok">
                <p className="m-0">
                  Sunucu iptali reddetti. Sayım, yetkili olmadığınız bir noktaya
                  ait olabilir.
                </p>
              </Uyari>
            ) : (
              <IslemDurumu hata={iptal.error} />
            )}

            {kapat.error &&
            (yetkisizMi(kapat.error) || kapsamDisiMi(kapat.error)) ? (
              <Uyari tur="hata" baslik="Sayımı kapatma yetkiniz yok">
                <p className="m-0">
                  Sunucu kapanışı reddetti. Sayım, yetkili olmadığınız bir
                  noktaya ait olabilir; kapanışı o noktanın sorumlusu veya
                  kantin müdürü yapmalıdır.
                </p>
              </Uyari>
            ) : (
              <IslemDurumu hata={kapat.error} />
            )}

            {s.acik && !kapat.data && !iptal.data && (
              <div className="border-t border-cetvel pt-3">
                {iptalOnayAcik ? (
                  <TehlikeliOnay
                    soru="Sayımı fark yazmadan iptal etmek istiyor musunuz?"
                    aciklama={
                      <p className="m-0">
                        Stok değişmez, hiçbir hareket yazılmaz. Sayılan
                        miktarlar denetim için saklanır ama bir daha işlenemez;
                        rafı yeniden saymanız gerekir.
                      </p>
                    }
                    onayMetni="Evet, iptal et"
                    onayla={() => iptal.mutate()}
                    vazgec={() => setIptalOnayAcik(false)}
                    yukleniyor={iptal.isPending}
                  />
                ) : onayAcik ? (
                  <TehlikeliOnay
                    soru={
                      farkliSatirlar.length === 0
                        ? "Sayımı kapatmak istiyor musunuz?"
                        : `${farkliSatirlar.length} satırda fark var; kapanışta stok hareketi yazılacak ve geri alınamaz.`
                    }
                    aciklama={
                      <p className="m-0">
                        Kapanışta her farklı satır için bir{" "}
                        <strong>sayım farkı</strong> hareketi yazılır; defter
                        rafa eşitlenir ve sapma noktanın sorumluluğuna geçer
                        (KK-520 / KK-521). Kapanan sayım yeniden işlenemez.
                      </p>
                    }
                    onayMetni="Evet, kapat ve farkı yaz"
                    onayla={() => kapat.mutate()}
                    vazgec={() => setOnayAcik(false)}
                    yukleniyor={kapat.isPending}
                  />
                ) : (
                  <div className="flex flex-wrap items-center gap-2">
                    <Buton tur="tehlike" onClick={() => setOnayAcik(true)}>
                      Sayımı kapat ve farkı yaz
                    </Buton>
                    <Buton tur="ikincil" onClick={() => setIptalOnayAcik(true)}>
                      Sayımı iptal et
                    </Buton>
                    <p className="m-0 text-xs text-murekkep-3">
                      Sayım açık kaldığı sürece stok değişmez; fark yazılmamış
                      demektir. İptal, farkı hiç yazmadan kapatır.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </SorguDurumu>
    </Kart>
  );
}

// ---------------------------------------------------------------------------
// Yeni sayım
// ---------------------------------------------------------------------------

interface TaslakSatiri {
  /** React anahtarı — elle eklenen satırda ürün henüz seçilmemiş olabilir. */
  anahtar: string;
  urunId: string;
  stokDurumu: number;
  /** Ön-doldurulan satırda sistemdeki miktar; elle eklenende bilinmez. */
  sistemMiktar: string | null;
  /** Ön-doldurulan satırın adı; elle eklenende Secim gösterir. */
  urunAd: string | null;
  sayilan: string;
}

function YeniSayim({ olusturuldu }: { olusturuldu: (id: string) => void }) {
  const istemci = useQueryClient();
  const noktalar = useNoktalar({ aktif: true });
  const urunler = useUrunler({ aktif: true });

  const [noktaId, setNoktaId] = useState("");
  const [aciklama, setAciklama] = useState("");
  const [satirlar, setSatirlar] = useState<TaslakSatiri[]>([]);
  const [gonderildi, setGonderildi] = useState(false);
  // Son okutulan barkodun satırı (anahtar). Vurgu + odak bu satıra gider.
  const [vurgulanan, setVurgulanan] = useState<string | null>(null);
  const sayilanAlanlari = useRef(new Map<string, HTMLInputElement>());

  const elleSayac = useRef(0);
  // Ön-doldurmanın HANGİ nokta için yapıldığı. Sorgu arka planda tazelenince
  // kullanıcının yazdığı sayımlar EZİLMEMELİDİR; bu yüzden doldurma nokta
  // başına tek sefer yapılır.
  const dolduruldu = useRef<string | null>(null);

  const stokSorgu = useQuery({
    queryKey: ["stok", "sayim-on-doldurma", noktaId],
    queryFn: () =>
      apiOku<{ urunler: StokUrunu[] }>("/stok", { nokta_id: noktaId }),
    enabled: noktaId !== "",
  });

  // Ön-doldurma: okulun TÜM aktif ürünleri + noktada stok kaydı olan
  // kondisyonlar. GET /stok yalnızca sıfırdan farklı kayıtları döndürür
  // (v_stok_dagilimi · miktar <> 0); stoğu sıfır olan ürün orada yoktur ama
  // rafta bulunabilir ve sayılmalıdır. Stok kaydı olmayan ürün SAĞLAM
  // satırıyla, sistem 0 olarak gelir; sunucu da eksik kayıt için 0 okur.
  useEffect(() => {
    if (noktaId === "") {
      dolduruldu.current = null;
      setSatirlar([]);
      setVurgulanan(null);
      return;
    }
    const stok = stokSorgu.data;
    const aktifUrunler = urunler.data?.urunler;
    if (!stok || !aktifUrunler || dolduruldu.current === noktaId) return;
    dolduruldu.current = noktaId;

    const yeni: TaslakSatiri[] = [];
    const saglamiVar = new Set<string>();
    for (const u of stok.urunler) {
      for (const d of u.dagilim) {
        if (d.nokta_id !== noktaId) continue;
        if (d.durum === StokDurumu.SAGLAM) saglamiVar.add(u.urun_id);
        yeni.push({
          anahtar: `${u.urun_id}|${d.durum}`,
          urunId: u.urun_id,
          stokDurumu: d.durum,
          sistemMiktar: d.miktar,
          urunAd: u.urun_ad,
          sayilan: "",
        });
      }
    }
    for (const u of aktifUrunler) {
      if (saglamiVar.has(u.id)) continue;
      yeni.push({
        anahtar: `${u.id}|${StokDurumu.SAGLAM}`,
        urunId: u.id,
        stokDurumu: StokDurumu.SAGLAM,
        sistemMiktar: "0",
        urunAd: u.ad,
        sayilan: "",
      });
    }
    yeni.sort(
      (a, b) =>
        (a.urunAd ?? "").localeCompare(b.urunAd ?? "", "tr") ||
        a.stokDurumu - b.stokDurumu,
    );
    setSatirlar(yeni);
    setGonderildi(false);
  }, [noktaId, stokSorgu.data, urunler.data]);

  const olustur = useMutation({
    mutationFn: (gonderilecek: TaslakSatiri[]) =>
      apiYaz<{ sayim_id: string }>("POST", "/sayim", {
        nokta_id: noktaId,
        ...(aciklama.trim() ? { aciklama: aciklama.trim() } : {}),
        // Sistem miktarı GÖNDERİLMEZ: sunucu o anki değeri kendisi okur.
        satirlar: gonderilecek.map((s) => ({
          urun_id: s.urunId,
          stok_durumu: s.stokDurumu,
          sayilan_miktar: miktarNormalize(s.sayilan),
        })),
      }),
    onSuccess: (y) => {
      // Sayımı AÇMAK stoğa dokunmaz (fark hareketi kapanışta yazılır), bu
      // yüzden ["stok"] geçersizleştirilmez: GET /stok `agir: true` bir
      // sorgudur ve boşuna çalıştırılmaz.
      void istemci.invalidateQueries({ queryKey: ["sayim"] });
      olusturuldu(y.sayim_id);
    },
  });

  // Sayılan miktar 0 OLABİLİR ("rafta hiç yok" bilgisi sayımın kendisidir),
  // boş olamaz: boş satır sayılmamış satırdır ve gönderilmez.
  const gonderilecek = satirlar.filter(
    (s) => s.urunId !== "" && miktarGecerliMi(s.sayilan),
  );
  const bosSatirSayisi = satirlar.filter(
    (s) => s.urunId !== "" && s.sayilan.trim() === "",
  ).length;
  const gecersizSatirVar = satirlar.some(
    (s) =>
      s.urunId !== "" && s.sayilan.trim() !== "" && !miktarGecerliMi(s.sayilan),
  );
  // Elle eklenmiş, sayılanı yazılmış ama ÜRÜNÜ seçilmemiş satır: gönderilecek
  // kümeye de boş sayacına da girmez, yani sessizce kaybolur. Sayılamaz.
  const urunsuzSatirVar = satirlar.some(
    (s) => s.urunId === "" && s.sayilan.trim() !== "",
  );

  // Sunucu urun_id|stok_durumu çiftinin tekrarsız olmasını ister
  // (servisler/stok.ts · tekrarsizDogrula). Gönderimden ÖNCE yakalanır.
  const gorulen = new Set<string>();
  const tekrarEdenler = new Set<string>();
  for (const s of gonderilecek) {
    const cift = `${s.urunId}|${s.stokDurumu}`;
    if (gorulen.has(cift)) tekrarEdenler.add(cift);
    else gorulen.add(cift);
  }

  const urunAdi = (id: string): string =>
    (urunler.data?.urunler ?? []).find((u) => u.id === id)?.ad ?? id;

  const cokFazlaSatir = gonderilecek.length > SATIR_UST_SINIR;
  const gonderilebilir =
    noktaId !== "" &&
    gonderilecek.length > 0 &&
    !gecersizSatirVar &&
    !urunsuzSatirVar &&
    tekrarEdenler.size === 0 &&
    !cokFazlaSatir;

  // Okutulan satırın sayılan alanına odaklan. Yeni açılan satırda input ancak
  // render sonrası var olur; bu yüzden odak effect'te verilir.
  useEffect(() => {
    if (vurgulanan === null) return;
    const alan = sayilanAlanlari.current.get(vurgulanan);
    if (!alan) return;
    alan.focus();
    alan.select();
    alan.scrollIntoView({ block: "nearest" });
  }, [vurgulanan, satirlar.length]);

  const satirGuncelle = (i: number, yama: Partial<TaslakSatiri>) =>
    setSatirlar((o) => o.map((x, j) => (j === i ? { ...x, ...yama } : x)));

  /**
   * KK-503 · barkod okutarak sayma. Okutma satırı BULUR, saymaz: satır
   * vurgulanır ve sayılan alanına odaklanılır; miktarı kullanıcı elle yazar.
   * Okuyucunun aynı barkodu yanlışlıkla iki kez okuması ya da koli üstünden
   * tek okutma ile 24 adet sayma ihtiyacı, "her okutma +1" kuralını
   * güvenilmez kılıyordu.
   *
   * KONDİSYON KARARI: okutma her zaman SAĞLAM satırı hedefler. Barkod malın
   * ne olduğunu söyler, hangi hâlde olduğunu söylemez; rafta duran ve
   * satılabilir olan mal sağlamdır. Diğer kondisyonlar bilinçli bir karardır
   * ve elle eklenen satırdan girilir.
   *
   * DEFTERDE OLMAYAN MAL: okutulan ürünün o noktada satırı yoksa satır burada
   * AÇILIR ve sistem miktarı "—" kalır. Sayımın asıl bulduğu şey budur; bu
   * satırı gizlemek, farkı gizlemek demektir (KK-520).
   */
  const barkodOkut = (barkod: string): BarkodSonucu => {
    if (urunler.isPending) {
      return {
        tur: "hata",
        mesaj: "Ürün listesi henüz yüklenmedi; bir an sonra tekrar okutun.",
      };
    }
    const urun = barkodBul(urunler.data?.urunler, barkod);
    if (!urun) {
      setVurgulanan(null);
      return {
        tur: "hata",
        mesaj: `"${barkod}" tanımlı bir ürün barkodu değil. Barkodu Ürünler ekranından ürüne ekleyin; pasif ürünler bu listede yer almaz.`,
      };
    }

    const hedef = satirlar.find(
      (s) => s.urunId === urun.id && s.stokDurumu === StokDurumu.SAGLAM,
    );

    if (hedef) {
      setVurgulanan(hedef.anahtar);
      // Aynı satır art arda okutulunca state değişmez; odak yine verilmeli.
      const alan = sayilanAlanlari.current.get(hedef.anahtar);
      alan?.focus();
      alan?.select();
      alan?.scrollIntoView({ block: "nearest" });
      return {
        tur: "onay",
        mesaj: `${urun.ad} · satır bulundu, sayılan miktarı yazın.`,
      };
    }

    elleSayac.current += 1;
    const anahtar = `elle-${elleSayac.current}`;
    setSatirlar((o) => [
      ...o,
      {
        anahtar,
        urunId: urun.id,
        stokDurumu: StokDurumu.SAGLAM,
        sistemMiktar: null,
        urunAd: urun.ad,
        sayilan: "",
      },
    ]);
    setVurgulanan(anahtar);
    return {
      tur: "onay",
      mesaj: `${urun.ad} · defterde yok, yeni satır açıldı; sayılan miktarı yazın.`,
    };
  };

  return (
    <Kart baslik="Yeni sayım">
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          setGonderildi(true);
          if (!gonderilebilir) return;
          olustur.mutate(gonderilecek);
        }}
      >
        <Uyari tur="bilgi" baslik="Bu adım stoğa dokunmaz">
          Sayımı açmak yalnızca{" "}
          <strong>o anki sistem miktarını dondurur</strong> ve farkı gösterir.
          Stok hareketi, farkı gördükten sonra
          <strong> kapanış adımında</strong> yazılır.
        </Uyari>

        <div className="grid gap-3 md:grid-cols-2">
          <Secim
            etiket="Sayılan nokta"
            value={noktaId}
            // Sıfırlama SENKRON olmalıdır. Nokta değişince queryKey de değişir
            // ve `stokSorgu.data` bir tur `undefined` olur; ön-doldurma effect'i
            // o turda erken çıkar. Satırlar temizlenmezse ESKİ noktanın
            // kalemleri, kullanıcının yazdığı sayımlarla birlikte ekranda kalır
            // ve "Sayımı aç" düğmesi (iskeletin dışında) tıklanabilir olduğu
            // için YENİ noktaya yazılabilir: sunucu eksik ürün için sistem
            // miktarını 0 okur ve sayılanın tamamını fark yazar.
            onChange={(e) => {
              dolduruldu.current = null;
              setSatirlar([]);
              setGonderildi(false);
              setNoktaId(e.target.value);
            }}
            secenekler={noktaSecenekleri(noktalar.data?.noktalar, "Seçiniz")}
          />
        </div>

        {gonderildi && noktaId === "" && (
          <p className="m-0 text-xs text-hata" role="alert">
            Sayım nokta bazlıdır; önce bir nokta seçin.
          </p>
        )}

        {noktaId !== "" && (
          <section aria-label="Sayım satırları" className="flex flex-col gap-2">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <h3 className="m-0 text-lg">Satırlar</h3>
              <p className="m-0 text-xs text-murekkep-3">
                Sistem miktarı yalnızca <strong>neyi doğruladığınızı</strong>{" "}
                görmeniz için gösterilir; sunucuya gönderilmez.
              </p>
            </div>

            <div className="grid gap-3 md:grid-cols-2">
              <BarkodAlani
                onOkut={barkodOkut}
                pasif={urunler.isPending || urunler.isError}
                pasifMetni={urunler.isError ? "Ürün listesi yüklenemedi." : "Ürün listesi yükleniyor…"}
                ipucu="Okutma ürünün SAĞLAM satırını bulup vurgular; miktarı siz yazarsınız. Diğer kondisyonlar için satırı elle ekleyin. Kısayol: F2."
              />
            </div>

            <SorguDurumu
              yukleniyor={stokSorgu.isPending || urunler.isPending}
              hata={stokSorgu.error ?? urunler.error}
              bos={satirlar.length === 0}
              bosBaslik="Sayılacak ürün yok"
              bosAciklama="Okulda aktif ürün ve bu noktada stok kaydı bulunmuyor. Rafta mal varsa aşağıdan satır ekleyip sayabilirsiniz."
              iskeletSatir={4}
              yenile={() => {
                void stokSorgu.refetch();
                void urunler.refetch();
              }}
            >
              <Tablo>
                <TabloBaslik>
                  <th scope="col">Ürün</th>
                  <th scope="col">Kondisyon</th>
                  <th scope="col">Sistem</th>
                  <th scope="col">Sayılan</th>
                  <th scope="col">
                    <span className="gizli-erisilebilir">İşlem</span>
                  </th>
                </TabloBaslik>
                <TabloGovde>
                  {satirlar.map((s, i) => {
                    const cift = `${s.urunId}|${s.stokDurumu}`;
                    const tekrar = s.urunId !== "" && tekrarEdenler.has(cift);
                    const miktarHatasi =
                      s.sayilan.trim() !== "" && !miktarGecerliMi(s.sayilan)
                        ? "Miktarı 5 veya 5,500 biçiminde girin."
                        : undefined;
                    const vurgulu = s.anahtar === vurgulanan;
                    return (
                      <tr
                        key={s.anahtar}
                        className={vurgulu ? "bg-simit-yumusak" : undefined}
                        aria-current={vurgulu ? "true" : undefined}
                      >
                        <td className="font-semibold text-murekkep">
                          {s.urunAd !== null ? (
                            s.urunAd
                          ) : (
                            <UrunSecimi
                              etiket={`Satır ${i + 1} ürün`}
                              deger={s.urunId}
                              degistir={(urunId) =>
                                satirGuncelle(i, { urunId })
                              }
                              urunler={urunler.data?.urunler}
                              pasif={urunler.isPending || urunler.isError}
                            />
                          )}
                          {tekrar && (
                            <p
                              className="m-0 mt-1 text-xs text-hata"
                              role="alert"
                            >
                              {urunAdi(s.urunId)} · {kondisyonAdi(s.stokDurumu)}{" "}
                              birden çok satırda var. Aynı ürün ve kondisyon
                              yalnızca bir kez sayılabilir.
                            </p>
                          )}
                        </td>
                        <td>
                          {s.urunAd !== null ? (
                            kondisyonAdi(s.stokDurumu)
                          ) : (
                            <Secim
                              etiket={`Satır ${i + 1} kondisyon`}
                              value={String(s.stokDurumu)}
                              onChange={(e) =>
                                satirGuncelle(i, {
                                  stokDurumu: Number.parseInt(
                                    e.target.value,
                                    10,
                                  ),
                                })
                              }
                              secenekler={Object.entries(STOK_DURUM_ADI).map(
                                ([k, v]) => ({ deger: k, metin: v }),
                              )}
                            />
                          )}
                        </td>
                        <td className="rakam text-right font-mono">
                          {s.sistemMiktar === null
                            ? "—"
                            : miktarGoster(s.sistemMiktar)}
                        </td>
                        <td>
                          <Alan
                            ref={(el) => {
                              if (el)
                                sayilanAlanlari.current.set(s.anahtar, el);
                              else sayilanAlanlari.current.delete(s.anahtar);
                            }}
                            etiket={`${
                              s.urunAd ?? `Satır ${i + 1}`
                            } sayılan miktar`}
                            value={s.sayilan}
                            onChange={(e) =>
                              satirGuncelle(i, { sayilan: e.target.value })
                            }
                            inputMode="decimal"
                            className="max-w-[8rem] text-right"
                            {...(miktarHatasi ? { hata: miktarHatasi } : {})}
                          />
                        </td>
                        <td className="text-right">
                          <Buton
                            type="button"
                            tur="sessiz"
                            onClick={() =>
                              setSatirlar((o) => o.filter((_, j) => j !== i))
                            }
                            aria-label={`${
                              s.urunAd ?? `Satır ${i + 1}`
                            } satırını kaldır`}
                          >
                            Kaldır
                          </Buton>
                        </td>
                      </tr>
                    );
                  })}
                </TabloGovde>
              </Tablo>
            </SorguDurumu>

            <div className="flex flex-wrap items-center gap-2">
              <Buton
                type="button"
                tur="ikincil"
                onClick={() => {
                  elleSayac.current += 1;
                  setSatirlar((o) => [
                    ...o,
                    {
                      anahtar: `elle-${elleSayac.current}`,
                      urunId: "",
                      stokDurumu: StokDurumu.SAGLAM,
                      sistemMiktar: null,
                      urunAd: null,
                      sayilan: "",
                    },
                  ]);
                }}
              >
                Satır ekle
              </Buton>
              <p className="m-0 text-xs text-murekkep-3">
                Defterde görünmeyen ama rafta olan mal buradan eklenir.
              </p>
            </div>

            <p className="m-0 text-sm text-murekkep-2" role="status">
              <span className="rakam font-mono">{gonderilecek.length}</span>{" "}
              satır gönderilecek
              {bosSatirSayisi > 0 && (
                <>
                  {" · "}
                  <span className="rakam font-mono">{bosSatirSayisi}</span>{" "}
                  satır boş bırakıldığı için sayıma girmeyecek
                </>
              )}
              . Rafta hiç yoksa <strong>0</strong> yazın; boş bırakmak
              &quot;saymadım&quot; demektir.
            </p>
            {gonderildi && urunsuzSatirVar && (
              <p className="m-0 mt-2 text-xs text-hata" role="alert">
                Sayılan miktarı yazılmış ama ürünü seçilmemiş satır var. Bu
                satır sessizce atlanmaz — ürünü seçin veya satırı kaldırın.
              </p>
            )}

            {cokFazlaSatir && (
              <p className="m-0 text-xs text-hata" role="alert">
                Bir sayımda en fazla{" "}
                <span className="rakam font-mono">{SATIR_UST_SINIR}</span> satır
                gönderilebilir. Sayımı kondisyona veya ürün grubuna göre bölün.
              </p>
            )}

            {tekrarEdenler.size > 0 && (
              <p className="m-0 text-xs text-hata" role="alert">
                Aynı ürün ve kondisyon birden çok satırda var. Tekrar eden
                satırları birleştirin veya kaldırın.
              </p>
            )}

            {gonderildi && gonderilecek.length === 0 && (
              <p className="m-0 text-xs text-hata" role="alert">
                En az bir satırda sayılan miktar girin.
              </p>
            )}
          </section>
        )}

        <MetinAlani
          etiket="Açıklama"
          value={aciklama}
          onChange={(e) => setAciklama(e.target.value)}
          rows={2}
          ipucu="Örnek: Hafta sonu kapanış sayımı, dolap 2 dahil."
        />

        <IslemDurumu hata={olustur.error} />

        <div className="flex flex-wrap items-center gap-2">
          <Buton type="submit" tur="birincil" yukleniyor={olustur.isPending}>
            Sayımı aç
          </Buton>
          <p className="m-0 text-xs text-murekkep-3">
            Açılan sayım stok yazmaz; fark tablosunu gördükten sonra
            kapatırsınız.
          </p>
        </div>
      </form>
    </Kart>
  );
}

// ---------------------------------------------------------------------------
// Geçmiş
// ---------------------------------------------------------------------------

function GecmisSayimlar({
  acik,
  ac,
}: {
  acik: string | null;
  ac: (id: string | null) => void;
}) {
  const [sayfa, setSayfa] = useState(1);
  const [noktaId, setNoktaId] = useState("");
  const [durum, setDurum] = useState<number>(SAYIM_KAPANDI);
  const noktalar = useNoktalar();

  const sorgu = useQuery({
    queryKey: ["sayim", "liste", durum, noktaId, sayfa],
    queryFn: () =>
      apiOku<{ sayimlar: SayimBasligi[] }>("/sayim", {
        durum,
        nokta_id: noktaId || undefined,
        // Bir FAZLA satır istenir: sunucu toplam sayı döndürmediği için
        // "sonraki sayfa var mı" ancak böyle bilinir.
        limit: GECMIS_BOYUT + 1,
        offset: (sayfa - 1) * GECMIS_BOYUT,
      }),
  });

  const gelen = sorgu.data?.sayimlar ?? [];
  const dahaVar = gelen.length > GECMIS_BOYUT;
  const sayimlar = dahaVar ? gelen.slice(0, GECMIS_BOYUT) : gelen;
  // Sunucu toplam vermez; gösterilen sayı bir ALT SINIRDIR ve yalnızca
  // "sonraki" düğmesinin etkinliğini doğru belirlemek için üretilir.
  const toplam =
    (sayfa - 1) * GECMIS_BOYUT + sayimlar.length + (dahaVar ? 1 : 0);

  return (
    <>
      <Kart baslik="Süzgeçler">
        <div className="grid gap-3 md:grid-cols-3">
          <Secim
            etiket="Nokta"
            value={noktaId}
            onChange={(e) => {
              setNoktaId(e.target.value);
              setSayfa(1);
            }}
            secenekler={noktaSecenekleri(noktalar.data?.noktalar)}
          />
          <Secim
            etiket="Durum"
            value={String(durum)}
            onChange={(e) => {
              setDurum(Number.parseInt(e.target.value, 10));
              setSayfa(1);
            }}
            secenekler={[
              { deger: String(SAYIM_KAPANDI), metin: "Kapandı — fark yazıldı" },
              { deger: String(SAYIM_IPTAL), metin: "İptal — fark yazılmadı" },
            ]}
          />
        </div>
      </Kart>

      <Kart
        baslik={
          durum === SAYIM_IPTAL ? "İptal edilen sayımlar" : "Kapanmış sayımlar"
        }
      >
        <SorguDurumu
          yukleniyor={sorgu.isPending}
          hata={sorgu.error}
          bos={sayimlar.length === 0}
          bosBaslik={
            durum === SAYIM_IPTAL
              ? "İptal edilen sayım yok"
              : "Kapanmış sayım yok"
          }
          bosAciklama="Bu süzgeçte sayım bulunmuyor."
          yenile={() => void sorgu.refetch()}
        >
          <SayimTablosu sayimlar={sayimlar} acik={acik} ac={ac} kapanis />
          <Sayfalayici
            sayfa={sayfa}
            boyut={GECMIS_BOYUT}
            toplam={toplam}
            degistir={setSayfa}
          />
        </SorguDurumu>
      </Kart>

      {acik && <SayimDetayPaneli sayimId={acik} />}
    </>
  );
}
